import { Component, Input, ViewChild, ElementRef, OnInit } from '@angular/core';
import { CasingPlan2dSchematicComponent } from './casing-plan-2d-schematic.component';
import { CanvasModel, CanvasWellDataModel, CanvasPolyXYLocations, CanvasSchematicGround, CanvasKickPointModel, SchematicConstants, SchematicCanvasWellStringLengthModel, CanvasSchematicBoreHoleModel, CanvasSchematicStringLabel, CanvasMissingData } from '../canvas-object.model';
import { CasinPlanDiagramDirectionTypes } from "../casing-plan-diagram-direction-types.enum";
import { hasBorehole, isDefined } from "../../../new-casing-plan/models/casing-plan-functions.model";

@Component({
    selector: 'casing-plan-2d-schematic-directional',
    templateUrl: './casing-plan-2d-schematic-base.component.html',
    styleUrls: ['./casing-plan-2d-schematic.component.scss'],
    standalone: false
})

export class CasingPlan2dSchematicDirectionalComponent extends CasingPlan2dSchematicComponent implements OnInit {
    @Input() casingPlan: any = [];
    @Input() applicationData: any = {};
    @ViewChild('canvas', { static: true }) canvasRef: ElementRef;
    ctx: CanvasRenderingContext2D;
    canvasModel: CanvasModel = new CanvasModel();
    canvasWellDataModel: CanvasWellDataModel = new CanvasWellDataModel();
    canvasKickPointModel: CanvasKickPointModel = new CanvasKickPointModel();
    boreHoleArray: Array<CanvasSchematicBoreHoleModel> = new Array<CanvasSchematicBoreHoleModel>();
    schematicCanvasWellStringLengthModel: SchematicCanvasWellStringLengthModel = new SchematicCanvasWellStringLengthModel();
    canvasSchematicGround: CanvasSchematicGround = new CanvasSchematicGround();
    topStringCount: number = 0;
    bottomStringCount: number = 0;
    constructor() {
        super();
    }

    ngOnInit(): void {
        this.wellSegments = ((this.casingPlan != null) ? this.casingPlan.wellSegments.filter(w => w.position != null && w.position > 0) : []).sort((a, b) => {
            if (a.position < b.position) return -1;
            if (a.position > b.position) return 1;
            return 0;
        }); //only draw those segments with a known position
        this.setVariables();
    }

    setVariables(): void {

        //populate canvas values
        this.canvasModel.canvasRef = this.canvasRef;
        this.canvasWellDataModel.canvasRef = this.canvasRef;
        this.canvasSchematicGround.canvasRef = this.canvasRef;

        //populate application data
        this.canvasWellDataModel.wellName = this.applicationData.wellName;
        this.canvasWellDataModel.wellNumber = this.applicationData.wellNumber;
        this.canvasWellDataModel.applicationNumber = this.applicationData.applicationNumber;
        this.canvasWellDataModel.wellDirection = CasinPlanDiagramDirectionTypes.directional;
        this.canvasWellDataModel.setLocation(CanvasPolyXYLocations.bottomRight);

        //do we have a kick point?
        if (this.canvasKickPointModel.setFirstKickPoint(this.wellSegments)) {
            //set kickpoint to middle of canvas (standard)
            this.canvasKickPointModel.canvasXY = { X: this.canvasModel.canvasWidth / 2, Y: this.canvasModel.canvasHeight / 2 }
            //set vertical/diagonal feet to pixels (standard)
            this.canvasModel.verticalFeetToPixels = (this.canvasKickPointModel.canvasXY.Y - SchematicConstants.schematicTop) / (this.canvasKickPointModel.firstKickPointFt - this.casingPlan.wellSegments[0].boreholeTop);
            const deepestHole = Math.max.apply(Math, this.wellSegments.filter(h => isDefined(h.boreholeBottom)).map((h) => { return h.boreholeBottom; }));            
            this.canvasModel.diagonalFeetToPixels = (SchematicConstants.schematicBottom - this.canvasKickPointModel.canvasXY.Y) / (deepestHole - this.canvasKickPointModel.firstKickPointFt);

            //figure out if which segments fall before/after the kick point
            this.populateBoreHoleKickPointValues();

            //offset canvas kick point based on number of strings above and below the kick point
            // evens out room above and below for better lableling room!
            this.canvasKickPointModel.setDiagonalOffsetCanvasXY(this.casingPlan.wellSegments.length, this.topStringCount);

            //create x/y locations
            this.popluateLocationArrayValues();

            //draw on canvas
            this.updateCanvas();
        } else {
            //we don't have a kick point in wellSegments

            //create missing data object
            let missingData = new CanvasMissingData(this.canvasModel, this.dataMissingError);

            //add it to the canvas
            missingData.draw();
        }
    }

    /**
     * tracks number of segments above and below the kick point
     *  needed for average length calcs (all strings have average length - had nothing to do with actual string length)
     * */
    populateBoreHoleKickPointValues(): void {
        for (let index = 0; index < this.wellSegments.length; index++) {
            let segment = this.wellSegments[index];
            segment.index = index;
            let boreHoleString: CanvasSchematicBoreHoleModel = new CanvasSchematicBoreHoleModel(this.canvasModel, segment, this.canvasKickPointModel);
            if (boreHoleString.topSegment) this.topStringCount += 1;
            if (boreHoleString.bottomSegment) this.bottomStringCount += 1;
            //reverse the order so we can draw from outside in!
            this.boreHoleArray.unshift(boreHoleString);
            
        }
    }

    /**
     * calculates all poistions needed to draw complete canvas
     * */
    popluateLocationArrayValues(): void {
        for (let boreHole of this.boreHoleArray) {
            boreHole.setCanvasXY(boreHole, this.canvasKickPointModel, this.topStringCount, this.bottomStringCount, this.boreHoleArray.length);
        }
    }

   /**
     * populate the canvas
     * */
    updateCanvas(): void {
        this.addWhiteBackground(750, 750, this.canvasModel.ctx);
        //draw ground
        this.canvasSchematicGround.draw();
        //draw application number
        this.canvasWellDataModel.draw();
        //loop all segments
        for (let i = 0; i < this.boreHoleArray.length; i++) {
            let boreHoleString: CanvasSchematicBoreHoleModel = this.boreHoleArray[i];
            let precedingString: CanvasSchematicBoreHoleModel = (i + 1) < this.boreHoleArray.length ? this.boreHoleArray[i + 1] : null;
            boreHoleString.drawCement(boreHoleString);
            boreHoleString.drawCasing(boreHoleString);
            boreHoleString.drawShoes(boreHoleString);
            boreHoleString.drawLabels(boreHoleString, i + 1, precedingString);
            //special stuff for final (center) string
            if (boreHoleString.finalStringCasingCloseLocations.length > 0) {
                boreHoleString.drawFinalCasingClose(boreHoleString);
            }
            if (boreHoleString.finalStringShoeLocations.length > 0) {
                boreHoleString.drawFinalShoes(boreHoleString);
            }
        }
    }
}